import { useEffect, useMemo, useState } from "react";
import { Card, Table, Typography, message, Space, Tag } from "antd";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { fetchSystemConfig } from "../../../shared/lib/systemConfig";
import usePermissions from "../../../hooks/usePermissions";
import "../../../shared/styles/ListPages.css";

const { Title, Text } = Typography;

const SALES_ENDPOINTS = [
  (id) => `/products/${id}/sale-history`,
  (id) => `/products/${id}/sales-history`,
  (id) => `/products/${id}/history`,
];

const STOCK_ENDPOINTS = [
  (id) => `/products/${id}/stock-history`,
  (id) => `/products/${id}/stock-movements`,
  (id) => `/products/${id}/inventory-history`,
  (id) => `/products/${id}/history`,
];

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.history)) return payload.history;
  if (Array.isArray(payload?.sales)) return payload.sales;
  if (Array.isArray(payload?.stock_history)) return payload.stock_history;
  return [];
};

const endpointCache = new Map();

const fetchFirstAvailable = async (endpointFactories, productId) => {
  const cacheKey = `${productId}:${endpointFactories[0](productId)}`;
  const cachedFactory = endpointCache.get(cacheKey);
  if (cachedFactory) {
    try {
      const cachedResponse = await apiClient.get(cachedFactory(productId));
      return toArray(cachedResponse.data);
    } catch {
      endpointCache.delete(cacheKey);
    }
  }

  for (const endpointFactory of endpointFactories) {
    try {
      const res = await apiClient.get(endpointFactory(productId));
      endpointCache.set(cacheKey, endpointFactory);
      return toArray(res.data);
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.warn(`History endpoint failed: ${endpointFactory(productId)}`);
      }
    }
  }
  return [];
};

const fetchPurchaseOrderLines = async (productId) => {
  try {
    const res = await apiClient.get(`/products/${productId}/purchase-history`);
    const rows = res.data?.purchase_orders;
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    if (e?.response?.status !== 404) {
      console.warn("purchase-history failed", e);
    }
    return [];
  }
};

const ProductSaleHistory = () => {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const { hasPermission } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [productNameRaw, setProductNameRaw] = useState("N/A");
  const [salesHistory, setSalesHistory] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [poLines, setPoLines] = useState([]);
  const [mainCode, setMainCode] = useState("IQD");

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      try {
        const cfg = await fetchSystemConfig().catch(() => null);
        if (cfg?.currency_code) setMainCode(cfg.currency_code);

        const productRes = await apiClient.get(`/products/${id}`);
        setProductNameRaw(productRes?.data?.name || `#${id}`);

        const [sales, stock, poRows] = await Promise.all([
          fetchFirstAvailable(SALES_ENDPOINTS, id),
          fetchFirstAvailable(STOCK_ENDPOINTS, id),
          fetchPurchaseOrderLines(id),
        ]);

        setSalesHistory(sales);
        setStockHistory(stock);
        setPoLines(poRows);
      } catch (error) {
        console.error("Failed to load product history:", error);
        message.error(t("Error fetching product history"));
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [id, t]);

  const productName = useMemo(
    () => parseLocalizedName(productNameRaw, i18n.language, `#${id}`),
    [productNameRaw, i18n.language, id]
  );

  const poStatusColors = useMemo(
    () => ({
      draft: "default",
      ordered: "processing",
      received: "success",
      cancelled: "error",
      partial: "processing",
      paid: "success",
    }),
    []
  );

  const saleColumns = useMemo(
    () => [
      {
        title: t("id"),
        key: "index",
        render: (_, __, index) => index + 1,
      },
      {
        title: t("date"),
        key: "date",
        render: (_, record) => {
          const value = record.created_at || record.date || record.sold_at;
          return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
        },
      },
      {
        title: t("quantity"),
        key: "quantity",
        render: (_, record) =>
          record.quantity ?? record.qty ?? record.sold_quantity ?? "-",
      },
      {
        title: t("totalPrice"),
        key: "total",
        render: (_, record) => {
          const amount = record.total_price ?? record.total ?? record.amount;
          return amount !== undefined ? formatCurrency(amount, mainCode) : "-";
        },
      },
      {
        title: t("customer"),
        key: "customer",
        render: (_, record) =>
          record.customer_name || record.customer?.name || record.customer || "-",
      },
      {
        title: t("orders"),
        key: "order",
        render: (_, record) => {
          const orderId = record.order_id || record.order?.id;
          if (!orderId) return "-";
          if (!hasPermission("orders.view")) {
            return `#${orderId}`;
          }
          return (
            <Link to={`/TableOrder/InfoOrder/${orderId}`}>#{orderId}</Link>
          );
        },
      },
    ],
    [hasPermission, mainCode, t]
  );

  const stockColumns = useMemo(
    () => [
      {
        title: t("id"),
        key: "index",
        render: (_, __, index) => index + 1,
      },
      {
        title: t("date"),
        key: "date",
        render: (_, record) => {
          const value = record.created_at || record.date || record.updated_at;
          return value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-";
        },
      },
      {
        title: t("type"),
        key: "type",
        render: (_, record) => {
          const ty = String(record.type || record.action || record.movement_type || "").toLowerCase();
          if (ty === "addition") return t("stockMovementIn");
          if (ty === "removal") return t("stockMovementOut");
          return record.type || record.action || record.movement_type || "-";
        },
      },
      {
        title: t("quantity"),
        key: "quantity",
        render: (_, record) =>
          record.quantity ?? record.qty ?? record.change ?? record.delta ?? "-",
      },
      {
        title: t("productHistorySource"),
        key: "source",
        width: 200,
        render: (_, record) => {
          const poId = record.purchase_order_id;
          const orderId = record.order_id;
          const parts = [];
          if (poId) {
            if (hasPermission("purchase_orders.index")) {
              parts.push(
                <Link key={`po-${poId}`} to={`/purchase-orders/${poId}/edit`}>
                  PO #{poId}
                </Link>
              );
            } else {
              parts.push(<span key={`po-${poId}`}>PO #{poId}</span>);
            }
          }
          if (orderId) {
            if (hasPermission("orders.view")) {
              parts.push(
                <Link key={`ord-${orderId}`} to={`/TableOrder/InfoOrder/${orderId}`}>
                  #{orderId}
                </Link>
              );
            } else {
              parts.push(<span key={`ord-${orderId}`}>#{orderId}</span>);
            }
          }
          if (parts.length === 0) {
            return record.note || record.notes || record.reason || "—";
          }
          return <Space wrap size={4}>{parts}</Space>;
        },
      },
      {
        title: t("stock"),
        key: "stock",
        render: (_, record) =>
          record.current_stock ?? record.new_stock ?? record.stock_after ?? "-",
      },
      {
        title: t("notes"),
        key: "notes",
        ellipsis: true,
        render: (_, record) => record.note || record.notes || record.reason || "-",
      },
    ],
    [hasPermission, t]
  );

  const poColumns = useMemo(
    () => [
      {
        title: t("date"),
        key: "created_at",
        render: (_, row) =>
          row.created_at ? dayjs(row.created_at).format("YYYY-MM-DD HH:mm") : "—",
      },
      {
        title: t("purchaseOrdersColVendor"),
        key: "vendor_name",
        ellipsis: true,
        render: (_, row) => row.vendor_name || "—",
      },
      {
        title: "PO",
        key: "po",
        width: 96,
        render: (_, row) => {
          const pid = row.purchase_order_id;
          if (!pid) return "—";
          if (!hasPermission("purchase_orders.index")) {
            return `#${pid}`;
          }
          return (
            <Link to={`/purchase-orders/${pid}/edit`}>#{pid}</Link>
          );
        },
      },
      {
        title: t("purchaseOrdersColReference"),
        dataIndex: "reference",
        key: "reference",
        ellipsis: true,
        render: (v) => v || "—",
      },
      {
        title: t("quantity"),
        dataIndex: "quantity",
        key: "quantity",
        width: 88,
      },
      {
        title: t("purchaseOrdersLinePrice"),
        dataIndex: "single_price",
        key: "single_price",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersLineSubtotal"),
        dataIndex: "line_total",
        key: "line_total",
        render: (v) => formatCurrency(v, mainCode),
      },
      {
        title: t("purchaseOrdersColStatus"),
        dataIndex: "status",
        key: "status",
        render: (s) => (
          <Tag color={poStatusColors[s] || "default"}>{t(`poStatus_${s}`, { defaultValue: s })}</Tag>
        ),
      },
    ],
    [hasPermission, mainCode, poStatusColors, t]
  );

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("Product sale history")}
          </span>
        }
        variant="borderless"
      >
        <Title level={5} style={{ marginBottom: 6 }}>
          {productName}
        </Title>
        <Text type="secondary">{t("Full cycle for sales and stock updates")}</Text>

        <div style={{ marginTop: 18 }}>
          <Title level={5} style={{ marginBottom: 10 }}>
            {t("Sales history")}
          </Title>
          <Table
            loading={loading}
            dataSource={salesHistory.map((item, index) => ({
              ...item,
              key: item.id || `sale-${index}`,
            }))}
            columns={saleColumns}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 700 }}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Title level={5} style={{ marginBottom: 10 }}>
            {t("productHistoryPurchaseLines")}
          </Title>
          <Table
            loading={loading}
            dataSource={poLines.map((item, index) => ({
              ...item,
              key: item.purchase_order_id ? `po-${item.purchase_order_id}-${index}` : `pol-${index}`,
            }))}
            columns={poColumns}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 900 }}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Title level={5} style={{ marginBottom: 10 }}>
            {t("Stock history")}
          </Title>
          <Table
            loading={loading}
            dataSource={stockHistory.map((item, index) => ({
              ...item,
              key: item.id || `stock-${index}`,
            }))}
            columns={stockColumns}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 960 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ProductSaleHistory;
